<script lang="ts" setup>
import type { Address, RouterOutput } from '~/types'

interface AddressCoords {
  lat: string
  lng: string
}

const props = defineProps<{
  context: {
    value: Address | undefined | null
    node: { input: (v: Address | undefined | null) => unknown }
    placeholder?: string
    initialValue?: string
    disabled?: boolean
    showMapView?: boolean
    disableMaxWidth?: boolean
    disableExpand?: boolean
    showCoordinatesCheckbox?: boolean
  } & Record<string, unknown>
}>()

const rawAutoCompleteInput = ref(props.context.value?.label ?? props.context.initialValue ?? '')

const { suggestions, areSuggestionsLoading, formatOptionLabel, findAddressByLabel, findAddressById } = useAddressAutoComplete(rawAutoCompleteInput)
const options = computed(() => Object.values(suggestions.value).map(({ id, label }) => ({
  label,
  value: id,
})))

const isValidInput = computed(() => {
  return Boolean((rawAutoCompleteInput.value && props.context.value) || !rawAutoCompleteInput.value)
})

watch(suggestions, () => {
  const selectedAddress = findAddressByLabel(rawAutoCompleteInput.value)
  if (selectedAddress) {
    return updateAddress(selectedAddress)
  }
})

watch(() => rawAutoCompleteInput.value, () => {
  if (!rawAutoCompleteInput.value) {
    rawAutoCompleteInput.value = ''
    props.context.node.input(null)
  }
})

function handleSelect(id: string) {
  const selectedAddress = findAddressById(id)
  if (selectedAddress) {
    return updateAddress(selectedAddress)
  }
  return props.context.node.input(undefined)
}

const addressAddition = ref(props.context.value?.addition)
onMounted(() => addressAddition.value = props.context.value?.addition)
watch(addressAddition, (addition) => {
  if (props.context.value) {
    props.context.node.input({ ...props.context.value, addition })
  }
})

const hideCoordinates = toRef(props.context.value ?? { hideCoordinates: false }, 'hideCoordinates')
watch(hideCoordinates, (value) => {
  if (props.context.value) {
    props.context.node.input({ ...props.context.value, hideCoordinates: value })
  }
})

/** Map view */
const isMapExpanded = ref(false)
// Save the selected address to compare it with map marker
const selectedLocation = ref<AddressCoords | undefined >(
  props.context.value?.lat && props.context.value?.lng
    ? { lat: props.context.value.lat, lng: props.context.value.lng }
    : undefined,
)

const selectedLocationMarker = ref<AddressCoords | undefined >(
  props.context.value?.lat && props.context.value?.lng
    ? { lat: props.context.value.lat, lng: props.context.value.lng }
    : undefined,
)

function updateAddress(suggestion: Exclude<RouterOutput['address']['byQuery'][number], null>) {
  selectedLocation.value = suggestion.location
  selectedLocationMarker.value = suggestion.location
  props.context.node.input({
    label: suggestion.label,
    addition: addressAddition.value,
    ...suggestion.components,
    ...suggestion.location,
    hideCoordinates: hideCoordinates.value,
  })
}

function updateMarker(coords: AddressCoords) {
  if (props.context.value) {
    props.context.node.input({
      ...props.context.value,
      lat: coords.lat,
      lng: coords.lng,
    })
  }
  selectedLocationMarker.value = coords
}

watch(() => props.context.disabled, () => {
  if (props.context.disabled) {
    isMapExpanded.value = false
  }
})

const normalizedDeliveryCoords = computed(() => {
  if (!props.context.showMapView || !selectedLocationMarker.value?.lat || !selectedLocationMarker.value?.lng) {
    return undefined
  }
  return { lat: selectedLocationMarker.value.lat, lng: selectedLocationMarker.value.lng }
})
</script>

<template>
  <div class="space-y-3" :class="{ 'max-w-md': !context.disableMaxWidth }">
    <n-input-group>
      <n-auto-complete
        v-model:value="rawAutoCompleteInput"
        :input-props="{ autocomplete: 'disabled' }"
        :options="options"
        :loading="areSuggestionsLoading"
        :render-label="formatOptionLabel"
        :disabled="context.disabled"
        :placeholder="context.placeholder"
        :status="isValidInput ? undefined : 'warning'"
        size="large"
        clearable
        @select="handleSelect"
      />
      <n-button v-if="context.disableExpand" size="large" :disabled="!normalizedDeliveryCoords || context.disabled" @click="isMapExpanded = !isMapExpanded">
        <template #icon>
          <Icon :name="isMapExpanded ? 'material-symbols:collapse-all-rounded' : 'material-symbols:expand-all-rounded'" />
        </template>
      </n-button>
    </n-input-group>

    <div>
      <label class="col-span-2 space-y-1">
        <span class="font-semibold">
          {{ $t('address.addition.label') }}
        </span>
        <n-input
          v-model:value="addressAddition"
          size="large"
          :placeholder="$t('address.addition.placeholder')"
        />
      </label>
    </div>

    <template v-if="context.showMapView && normalizedDeliveryCoords">
      <ClientOnly>
        <div
          v-if="!context.disabled"
          class="relative rounded overflow-hidden transition-all"
          :class="{ 'h-[50px]': !isMapExpanded, 'h-[300px]': isMapExpanded }"
        >
          <MapView
            :key="`${JSON.stringify(selectedLocation)}`"
            :center="selectedLocation"
            :marker="normalizedDeliveryCoords"
            is-marker-draggable
            :zoom="15"
            :min-zoom="14"
            :max-zoom="18"
            :hide-controls="!isMapExpanded"
            :bound-radius-in-meters="500"
            :style="{ height: '300px', width: '100%' }"
            @update:marker="updateMarker"
          />

          <div v-if="isMapExpanded" class="absolute bottom-5 left-3 flex items-center gap-2">
            <div class="flex items-center gap-2 bg-white/75 rounded overflow-hidden py-1 px-2">
              <div class="size-5 bg-hos-blue-light rounded-full border-2 border-hos-blue/75" />
              <p class="text-base text-black">
                {{ $t('shop.searchbar.deliveryLocation.dropOffArea') }}
              </p>
            </div>
          </div>

          <div
            v-if="!isMapExpanded"
            class="absolute inset-0 bg-hos-blue/50 grid place-content-center cursor-pointer"
            @click="() => isMapExpanded = true"
          >
            <Icon name="material-symbols:expand-all-rounded" class="text-white" size="35" />
          </div>
        </div>
      </ClientOnly>

      <div v-if="!hideCoordinates" class="text-sm text-gray-500">
        Koordinaten genauer Abstellort:
        <NuxtLink
          :to="useGoogleMapsLink(`${normalizedDeliveryCoords.lat},${normalizedDeliveryCoords.lng}`)"
          target="_blank"
          class="underline"
        >
          {{ normalizedDeliveryCoords.lat }},{{ normalizedDeliveryCoords.lng }}
        </NuxtLink>
      </div>
    </template>

    <div v-if="context.showCoordinatesCheckbox">
      <FormKit
        v-model="hideCoordinates"
        type="checkbox"
        :label="$t('address.hideCoordinates.label')"
        :disabled="context.disabled"
      />
    </div>
  </div>
</template>
